/*  MUI  */
import { Dialog } from '@mui/material';

/*  Shared  */
import { Transition } from 'application/Shared/Helpers';

/*  React  */
import React from 'react';
import Draggable from 'react-draggable';

/* Components */
import { ModalTitle } from './ModalTitle';

// Taken from MUI Dialog docs. See 'onClose' prop. https://mui.com/material-ui/api/dialog/
const DIALOG_CLOSE_REASONS = ['escapeKeyDown', 'backdropClick'];
const defaultOnClose = () => {};

/**
 * A modal dialog component that can be made draggable
 * @param {import('./ModalType').ModalProps} props - The component props
 *
 * @returns {JSX.Element} The modal component
 */
export const Modal = (
    {
        children,
        open,
        onClose = defaultOnClose,
        // when 'true', won't allow modal to be closed by Escape keypress or background click.
        disableEasyClose = false,
        paperProps,
        title,
        draggable,
        titleProps,
        alignCenter = true,
        ...rest
    } // Make this component draggable
) => {
    // If running in React Strict mode, ReactDOM.findDOMNode() is deprecated.
    // Unfortunately, in order for <Draggable> to work properly, we need raw access
    // to the underlying DOM node. If you want to avoid the warning, pass a `nodeRef`
    // https://github.com/react-grid-layout/react-draggable
    const nodeRef = React.useRef(null);
    return (
        <Draggable
            nodeRef={nodeRef}
            handle='[class*="MuiDialog-root"]'
            cancel='[class*="MuiDialogContent-root"]'
            disabled={!draggable}
        >
            <div ref={nodeRef} style={{ display: 'none' }}>
                <Dialog
                    open={open}
                    scroll="body"
                    PaperProps={{ sx: { padding: 0, ...paperProps } }}
                    aria-labelledby="modalDialog"
                    TransitionComponent={Transition}
                    onClose={(e, reason) => {
                        if (
                            disableEasyClose &&
                            DIALOG_CLOSE_REASONS.includes(reason)
                        ) {
                            return;
                        }

                        onClose();
                    }}
                    {...rest}
                >
                    {title && (
                        <ModalTitle
                            title={title}
                            alignCenter={alignCenter}
                            {...titleProps}
                            onClose={onClose}
                        />
                    )}
                    {children}
                </Dialog>
            </div>
        </Draggable>
    );
};
